import axios from "axios";
import { Action, Module, Mutation, VuexModule, getModule } from "vuex-module-decorators"
import store from ".";
import { Meta } from "@/store/meta"
import router from "@/router";
import { RateLimitError } from "@/error/rate_limit_error";
export type ServerSummary = "OK" | "PROBLEM" | "DOWN"
import { config } from 'vuex-module-decorators'

config.rawError = true

export interface ServerResponse {
  successful: number;
  server_error: number;
  summary: ServerSummary;
}

axios.interceptors.response.use((response) => {
  return response
}, (error) => {
  if (axios.isAxiosError(error) && error.response) {
    console.warn(`failed to response with status code ${error.response.status}`)
    if (error.response.status == 401) {
      const targetUrl = error.response.config.url
      if (targetUrl?.endsWith("/account/token")) {
        // ignore redirect
        return Promise.reject(error)
      }
      console.log("session seems to be expired. redirect to login page.")
      router.push("/login")
    } else if (error.response.status == 429) {
      return Promise.reject(new RateLimitError())
    }
  }
  return Promise.reject(error)
});

export interface ServiceHealthResponse {
  // {"average_response_time":380.5,"platform_api":{"successful":100.0,"server_error":0.0,"summary":"OK"},"bocco_emo_api":{"successful":100.0,"server_error":0.0,"summary":"OK"},"webhook":"OK"}
  average_response_time: number;

  platform_api: ServerResponse;

  bocco_emo_api: ServerResponse;

  webhook: ServerSummary;
}

export interface LoginInfoResponse {
  name: string;

  name_furigana: string;

  organization_name: string;

  organization_unit_name: string;

  phone_number: string;

  email: string;

  profile_image: string;

  uuid: string;

  plan: string;

  privileged: boolean;

  role: "owner" | "member" | "viewer";

  /**
   * 所属組織 ID。owner / member / viewer が同じ組織を共有することを示す。
   * 旧フィールド `owner_account_id` は API 側 (organization 切出) で廃止された。
   * 「自分が owner かどうか」は `role === "owner"` で判定する。
   */
  organization_id: number;
}

export interface ChangePasswordPayload {
  current_password: string;
  new_password: string;
}

export interface Member {
  account_id: number;
  /** 同じ組織を共有することを示す (owner / member / viewer 全員で同値)。 */
  organization_id: number;
  name: string;
  name_furigana: string;
  email: string;
  organization_name: string;
  organization_unit_name: string;
  phone_number: string;
  role: "owner" | "member" | "viewer";
  status: string;
  privileged: boolean;
  plan: string;
  bocco_channel_plan: string;
}

export interface Invitation {
  invitation_id: number;
  email: string;
  role: "member" | "viewer";
  status: "pending" | "consumed" | "expired";
  expires_at: number;
  created_at: number;
}

export interface InvitationInfo {
  email: string;
  role: "member" | "viewer";
  organization_name: string;
  inviter_name: string;
  expires_at: number;
}

export interface CreateInvitationPayload {
  email: string;
  role: "member" | "viewer";
}

export interface AcceptInvitationPayload {
  password: string;
  name: string;
  name_furigana: string;
  phone_number: string;
}

export interface Partner {
  code: string;
  memo: string;
}

// GET /v1/rooms / /v1/rooms/{uuid} で取得する部屋情報。
// room_members は管理画面上ではプライバシー配慮で個人情報を露出させず、詳細画面での
// 「参加メンバー数」表示 (roomMembers.length) にのみ利用する。
export interface Room {
  uuid: string;
  name: string;
  room_type: string;
  room_members: { uuid: string }[];
  online: boolean;
  partner_code: string | null;
}

export interface RoomsResponse {
  listing: {
    offset: number;
    limit: number;
    total: number;
  };
  rooms: Room[];
}

// GET /v1/rooms/{uuid}/emo/settings のレスポンス。
// 詳細画面ではシリアルナンバーのみ表示するが、後日拡張の余地を残して全フィールドを保持する。
export interface EmoSetting {
  nickname: string;
  wakeword: string;
  volume: number;
  voice_pitch: number;
  voice_speed: number;
  lang: string;
  serial_number: string;
  timezone: string;
  zip_code: string;
}

export interface AccountInfo {
  name: string;

  name_furigana: string;

  email: string;

  organization_name: string;

  organization_unit_name: string;

  phone_number: string;
}

/**
 * 自分の個人情報 (commercial_account の identity 系列) のみの更新ペイロード。
 * PUT /v1/me/profile (全ロール) で送る。組織情報は含めない。
 */
export interface ProfileInfo {
  name: string;

  name_furigana: string;

  phone_number: string;
}

export interface AuthenticationResponse {
  access_token: string;

  refresh_token: string;
}

export interface IdPassAuthenticationPayload {
  email: string;

  password: string;
}

export interface PostBroadcastMessage {
  data: {
    title: string;
    text: string;
    executed_at?: number;
    immediate: boolean;
    partner_codes?: string[];
  };
  x_channle_user: string;
}

export interface BroadcastMessagesDataResponse {
  listing: {
    offset: number;
    limit: number;
    total: number;
  };
  messages: {
    // eslint-disable-next-line
    channel_uuid: string;
    id: number;
    title: string;
    text: string;
    executed_at: number;
    finished: boolean;
    success: number;
    failed: number;
  }[];
}

export interface BroadcastMessagesDataDetailResponse {
  message: {
    id: number;
    title: string;
    text: string;
    executed_at: number;
    finished: boolean;
    success: number;
    failed: number;
    partner_codes?: string[] | null;
  };
  details: {
    room_uuid: string;
    room_name: string;
    success: boolean;
    status_code: number;
    description: string;
    executed_at: number;
    partner_code: string | null;
  }[];
}

export interface CreateChannel {
  name: string;
  description: string;
  icon: File | undefined;
}

export interface EditChannelRequestForm {
  name: string;
  description: string;
  icon: File | undefined;
}

export interface BoccoChannel {
  channel: {
    name: string;
    description: string;
    uuid: string;
    icon: string;
  } | null;
  services: 
    {
      uuid: string;
      channel_code: string;
      name: string;
      description: string;
      icon: string;
      url: string;
      published: boolean;
      conversation_event_url: string | null;
      vui_enabled: boolean;
      voice_command: string;
    }[];
}

export interface BoccoChannelServiceUser {
  uuid: string;
  nickname: string;
  // eslint-disable-next-line
  profile_image: string;
}

export interface CreateChannelService {
  broad_caster_name: string;
  service_name: string;
  service_code: string;
  service_description: string;
  channel_page_url: string;
  icon: File | undefined;
  use_voice_command: boolean;
  word: string | undefined;
}

export interface EditServiceForm {
  uuid: string;
  data: {
    name: string;
    description: string;
    icon: File | undefined;// string($binary)
    url: string | undefined;
    published: boolean;
  };
}

export interface EditServiceVUIForm {
  uuid: string;
  voiceCommand: string;
}

export interface EditServiceWebhookForm {
  uuid: string;
  webhook: string;
}

export interface EditServiceUserForm {
  uuid: string;
  data: {
    name: string;
    icon: File;// string($binary)
  };
}

export interface PaymentsDataResponse {
  /**
   * Legacy alias of `organization_id`. API 側で organization 切出後も後方互換のため
   * 同値で並走している。新規実装は `organization_id` を参照することを推奨。
   */
  account_id: number;
  organization_id?: number;
  amount: number;
  created_at: number;
  currency: string;
  due_date: number;
  id: number;
  month: number;
  paid: boolean;
  payment_method: string;
  plan: string;
  updated_at: number;
  year: number;
}

export interface PaymentsDetailDataResponse {
  /** Legacy alias of `organization_id` (詳細は PaymentsDataResponse 参照)。 */
  account_id: number;
  organization_id?: number;
  amount: number;
  created_at: number;
  currency: string;
  due_date: number;
  id: number;
  month: number;
  paid: boolean;
  payment_method: string;
  plan: string;
  updated_at: number;
  year: number;
}

export interface PaymentsDataOption {
  total: number;
  limit: number;
}

@Module({ dynamic: true, store: store, name: 'ServerModule' })

export class Server extends VuexModule {

  boccoEmoApiStatus: ServerResponse = {
    successful: 0,
    // eslint-disable-next-line
    server_error: 0,
    summary: "OK"
  }

  platformApiStatus: ServerResponse = {
    successful: 0,
    // eslint-disable-next-line
    server_error: 0,
    summary: "OK"
  }

  webhookStatus: ServerSummary = "OK"

  loginInfo: LoginInfoResponse = {
    email: '',
    name: '',
    // eslint-disable-next-line
    name_furigana: '',
    // eslint-disable-next-line
    organization_name: '',
    // eslint-disable-next-line
    organization_unit_name: '',
    // eslint-disable-next-line
    phone_number: '',
    // eslint-disable-next-line
    profile_image: '',
    uuid: '',
    plan: '',
    privileged: false,
    role: 'owner',
    // eslint-disable-next-line
    organization_id: 0,
  }

  partnersData: Partner[] = []

  // 部屋一覧 (/rooms 画面)。ページ切替のたびに @Action getRooms が上書きする。
  roomsData: RoomsResponse = {
    listing: {
      offset: 0,
      limit: 0,
      total: 0,
    },
    rooms: [],
  }

  // 部屋詳細 (/rooms/:uuid 画面)。詳細を開くたびに @Action getRoomDetail で上書きする。
  roomDetailData: Room | null = null

  // 部屋詳細画面の BOCCO emo 情報。emo 未登録の部屋では null になる (404 等をキャッチする)。
  emoSettingData: EmoSetting | null = null
  BroadcastMessagesData: BroadcastMessagesDataResponse = {
    listing: {
      offset: 0,
      limit: 0,
      total: 0,
    },
    messages: [{
      // eslint-disable-next-line
      channel_uuid: '',
      id: 0,
      title: '',
      text: '',
      // eslint-disable-next-line
      executed_at: 0,
      finished: false,
      success: 0,
      failed: 0
    }]
  }

  BroadcastMessagesHistoryData: BroadcastMessagesDataResponse = {
    listing: {
      offset: 0,
      limit: 0,
      total: 0,
    },
    messages: [{
      // eslint-disable-next-line
      channel_uuid: '',
      id: 0,
      title: '',
      text: '',
      // eslint-disable-next-line
      executed_at: 0,
      finished: false,
      success: 0,
      failed: 0
    }]
  }

  BroadcastMessagesDataDetail: BroadcastMessagesDataDetailResponse = {
    message: {
      id: 0,
      title: '',
      text: '',
      // eslint-disable-next-line
      executed_at: 0,
      finished: false,
      success: 0,
      failed: 0,
    },
    details: [{
      // eslint-disable-next-line
      room_uuid: '',
      // eslint-disable-next-line
      room_name: '',
      success: false,
      // eslint-disable-next-line
      status_code: 0,
      description: '',
      // eslint-disable-next-line
      executed_at: 0,
      // eslint-disable-next-line
      partner_code: null
    }]
  }

  boccoChannel: BoccoChannel | null =  null

  currentBoccoChannelServiceUser: BoccoChannelServiceUser = {
    uuid: '',
    nickname: '',
    "profile_image": ''
  }
  
  PaymentsDatum: PaymentsDataResponse = {
    // eslint-disable-next-line
    account_id: 0,
    amount: 0,
    // eslint-disable-next-line
    created_at: 0,
    currency: 'jpy',
    // eslint-disable-next-line
    due_date: 0,
    id: -1,
    month: 1,
    paid: false,
    // eslint-disable-next-line
    payment_method: '',
    plan: '',
    // eslint-disable-next-line
    updated_at: 0,
    year: 0
  }
  PaymentsData: PaymentsDataResponse[] = [
    this.PaymentsDatum
  ]
  PaymentsDataOption: PaymentsDataOption = {
    total: 50,
    limit: 50
  }

  PaymentsDetailData: PaymentsDetailDataResponse = {
    // eslint-disable-next-line
    account_id: 0,
    amount: 0,
    // eslint-disable-next-line
    created_at: 0,
    currency: 'jpy',
    // eslint-disable-next-line
    due_date: 0,
    id: -1,
    month: 1,
    paid: false,
    // eslint-disable-next-line
    payment_method: 'bank_transfer',
    plan: '',
    // eslint-disable-next-line
    updated_at: 0,
    year: 0
  }
  get baseUrl() {
    return process.env["VUE_APP_API_URL"]
  }

  @Mutation
  updateBoccoEmoApiStatus(response: ServerResponse) {
    Object.assign(this.boccoEmoApiStatus, response)
  }

  @Mutation
  updatePlatformApiStatus(response: ServerResponse) {
    Object.assign(this.platformApiStatus, response)
  }

  @Mutation
  updateWebhookStatus(status: ServerSummary) {
    this.webhookStatus = status
  }

  @Mutation
  updateLoginInfo(info: LoginInfoResponse) {
    Object.assign(this.loginInfo, info)
  }

  @Mutation
  updatePaymentsData(data: PaymentsDataResponse[]) {
    Object.assign(this.PaymentsData, data)
  }

  @Mutation
  updatePaymentsDataOption(option: PaymentsDataOption) {
    Object.assign(this.PaymentsDataOption, option)
  }

  @Mutation
  updatePaymentsDetailData(datum: PaymentsDataResponse) {
    Object.assign(this.PaymentsDatum, datum)
  }

  @Mutation
  updatePartnersData(data: Partner[]) {
    this.partnersData = data
  }

  @Mutation
  updateRoomsData(data: RoomsResponse) {
    this.roomsData = data
  }

  @Mutation
  updateRoomDetailData(data: Room | null) {
    this.roomDetailData = data
  }

  @Mutation
  updateEmoSettingData(data: EmoSetting | null) {
    this.emoSettingData = data
  }

  @Mutation
  updateBroadcastMessagesData(data: BroadcastMessagesDataResponse) {
    Object.assign(this.BroadcastMessagesData, data)
  }

  @Mutation
  updateBroadcastMessagesHistoryData(data: BroadcastMessagesDataResponse) {
    Object.assign(this.BroadcastMessagesHistoryData, data)
  }

  @Mutation
  updateBroadcastMessagesDataDetail(data: BroadcastMessagesDataDetailResponse) {
    Object.assign(this.BroadcastMessagesDataDetail, data)
  }

  @Mutation
  updateBoccoChannel(data: BoccoChannel) {
    this.boccoChannel = data
  }

  @Mutation
  updateBoccoChannelServiceUser(data: BoccoChannelServiceUser) {
    Object.assign(this.currentBoccoChannelServiceUser, data)
  }

  @Action
  async getStatus() {
    const response = await axios.get(`${this.baseUrl}/v1/health/service`)
    const health = response.data as ServiceHealthResponse

    this.updateBoccoEmoApiStatus(health.bocco_emo_api)
    this.updatePlatformApiStatus(health.platform_api)
    this.updateWebhookStatus(health.webhook)
  }
  @Action
  async getAccounts() {
    const accessToken = getModule(Meta).accessToken
    const response = await axios.get(`${this.baseUrl}/admin/accounts`, {
      headers: {
        'accept': 'application/json',
        'Authorization': 'Bearer ' + accessToken
      }

    })
    return response.status
  }

  @Action
  async getPartners() {
    const accessToken = getModule(Meta).accessToken
    const response = await axios.get(`${this.baseUrl}/v1/partners`, {
      headers: {
        'accept': 'application/json',
        'Authorization': 'Bearer ' + accessToken
      }
    })
    const partners = response.data.partners as Partner[]
    this.updatePartnersData(partners)
    return partners
  }

  // GET /v1/rooms?offset=N — 部屋一覧を取得する。
  //
  // 個人アカウント: broadcasterUuid を省略。API 側の requiresBroadcaster フィルタは
  //   AccountType.FREE をパススルーするため、Bearer のみで参加部屋が返る。
  // 法人アカウント: 対象サービスの broadcasterUuid (= services[i].uuid) を渡す。
  //   requiresBroadcaster フィルタが X-Channel-User ヘッダを要求するため必須。
  //   1 リクエスト = 1 サービス分の購読部屋のみが返る。複数サービスをまとめて見たい場合は
  //   呼び出し側で複数回発火する必要がある。
  @Action
  async getRooms(payload: { offset?: number; broadcasterUuid?: string } = {}) {
    const offset = payload.offset ?? 0
    const accessToken = getModule(Meta).accessToken
    const headers: Record<string, string> = {
      'accept': 'application/json',
      'Authorization': 'Bearer ' + accessToken
    }
    if (payload.broadcasterUuid) {
      headers['X-Channel-User'] = payload.broadcasterUuid
    }
    const response = await axios.get(`${this.baseUrl}/v1/rooms?offset=${offset}`, { headers })
    const data = response.data as RoomsResponse
    this.updateRoomsData(data)
    return data
  }

  // GET /v1/rooms/{uuid} — 詳細画面用。broadcasterUuid の要否は getRooms と同じ規則。
  @Action
  async getRoomDetail(payload: { uuid: string; broadcasterUuid?: string }) {
    const accessToken = getModule(Meta).accessToken
    const headers: Record<string, string> = {
      'accept': 'application/json',
      'Authorization': 'Bearer ' + accessToken
    }
    if (payload.broadcasterUuid) {
      headers['X-Channel-User'] = payload.broadcasterUuid
    }
    const response = await axios.get(`${this.baseUrl}/v1/rooms/${payload.uuid}`, { headers })
    const data = response.data as Room
    this.updateRoomDetailData(data)
    return data
  }

  // GET /v1/rooms/{uuid}/emo/settings — 詳細画面のシリアルナンバー表示用。
  // BOCCO emo 未登録の部屋では 4xx が返り得るため、その場合は emoSettingData=null にして
  // 画面側で「未登録」と表示する (画面自体は成立させる)。broadcasterUuid の要否は getRooms と同じ。
  @Action
  async getEmoSettings(payload: { uuid: string; broadcasterUuid?: string }) {
    const accessToken = getModule(Meta).accessToken
    const headers: Record<string, string> = {
      'accept': 'application/json',
      'Authorization': 'Bearer ' + accessToken
    }
    if (payload.broadcasterUuid) {
      headers['X-Channel-User'] = payload.broadcasterUuid
    }
    try {
      const response = await axios.get(`${this.baseUrl}/v1/rooms/${payload.uuid}/emo/settings`, { headers })
      const data = response.data as EmoSetting
      this.updateEmoSettingData(data)
      return data
    } catch (error) {
      if (axios.isAxiosError(error)) {
        console.log(`failed to fetch emo settings for ${payload.uuid}. code = ${error.response?.status}`)
      }
      this.updateEmoSettingData(null)
      return null
    }
  }

  @Action
  async getBroadcastMessages(offset = 0) {
    const accessToken = getModule(Meta).accessToken
    const response = await axios.get(`${this.baseUrl}/v1/broadcast_messages?offset=${offset}`, {
      headers: {
        'accept': 'application/json',
        'Authorization': 'Bearer ' + accessToken
      }
    })

    const item = response.data
    this.updateBroadcastMessagesData(item)
    return true
  }

  @Action
  async getBroadcastMessagesHistory(offset = 0) {
    const accessToken = getModule(Meta).accessToken
    const response = await axios.get(`${this.baseUrl}/v1/broadcast_messages/history?offset=${offset}`, {
      headers: {
        'accept': 'application/json',
        'Authorization': 'Bearer ' + accessToken
      }
    })

    const item = response.data
    this.updateBroadcastMessagesHistoryData(item)
    return true
  }

  @Action
  async getBroadcastMessagesDetail(messageId: number) {
    const accessToken = getModule(Meta).accessToken
    try {
      const response = await axios.get(`${this.baseUrl}/v1/broadcast_messages/${messageId}`, {
        headers: {
          'accept': 'application/json',
          'Authorization': 'Bearer ' + accessToken
        }
      })

      const item = response.data as BroadcastMessagesDataDetailResponse
      this.updateBroadcastMessagesDataDetail(item)

      return item
    } catch (error) {
      if (axios.isAxiosError(error)) {
        console.log(`failed to fetch broadcast messages. code = ${error.response?.status}`)
      }

      return null
    }
  }

  @Action
  // eslint-disable-next-line
  async postBroadcastMessages(postBroadcastMessage: PostBroadcastMessage) {
    const accessToken = getModule(Meta).accessToken
    const sendData = postBroadcastMessage.data
    const xChannelUsers = postBroadcastMessage.x_channle_user
    if (sendData.immediate){
      delete sendData.executed_at
    }
    const response = await axios.post(`${this.baseUrl}/v1/broadcast_messages`, sendData, {
      headers: {
        'Content-Type': 'application/json',
        'accept': '*/*',
        'Authorization': 'Bearer ' + accessToken,
        'x-channel-user': xChannelUsers
      }
    })
    
    const status = response.status
    return status
  }

  @Action
  // eslint-disable-next-line
  async deleteBroadcastMessagesDetail(broadcast_message_id = 1) {
    const accessToken = getModule(Meta).accessToken
    // eslint-disable-next-line
    const response = await axios.delete(`${this.baseUrl}/v1/broadcast_messages/${broadcast_message_id}`, {
      headers: {
        'accept': 'application/json',
        'Authorization': 'Bearer ' + accessToken
      }
    })
    if (!response.status) {
      return false
    }
    return true
  }

  @Action
  async getBoccoChannel() {
    const accessToken = getModule(Meta).accessToken
    const response = await axios.get(`${this.baseUrl}/v1/bocco_channel`, {
      headers: {
        'accept': 'application/json',
        'Authorization': 'Bearer ' + accessToken
      }
    })
    const boccoChannel = response.data as BoccoChannel
    this.updateBoccoChannel(boccoChannel)
    if (response.status==200){
      return true
    }else{
      return false
    }
  }

  @Action
  async getBoccoChannelDestributionUser(channelUuid: string) {
    const accessToken = getModule(Meta).accessToken
    const response = await axios.get(`${this.baseUrl}/v1/bocco_channel/services/${channelUuid}/user`, {
      headers: {
        'accept': 'application/json',
        'Authorization': 'Bearer ' + accessToken
      }
    })

    this.updateBoccoChannelServiceUser(response.data as BoccoChannelServiceUser)
  }

  @Action
  async getBoccoChannelQRInformation(channelUuid: string) {
    const accessToken = getModule(Meta).accessToken
    const response = await axios.get(`${this.baseUrl}/v1/bocco_channel/services/${channelUuid}/qr`, {
      headers: {
        'accept': 'application/json',
        'Authorization': 'Bearer ' + accessToken
      }
    })

    return response.data
  }

  @Action
  async putBoccoChannel(payload: EditChannelRequestForm) {
    const accessToken = getModule(Meta).accessToken
    const data = new FormData() // for multipart/form-data
    
    data.append('name', payload.name)
    data.append('description', payload.description)
    if (payload.icon && payload.icon.size > 0)
      data.append('icon', payload.icon)

    const response = await axios.put(`${this.baseUrl}/v1/bocco_channel`, data, {
      headers: {
        'Accept': 'multipart/form-data',
        'Content-Type': 'multipart/form-data',
        'Authorization': 'Bearer ' + accessToken
      }
    })
    const status = response.status
    return status
  }

  @Action
  async postBoccoChannel(payload: CreateChannel) {
    const accessToken = getModule(Meta).accessToken
    const data = new FormData() 
    
    data.append('name', payload.name)
    data.append('description', payload.description)

    if(payload.icon && payload.icon.size != 0){
      data.append('icon', payload.icon)
    }

    const response = await axios.post(`${this.baseUrl}/v1/bocco_channel`, data, {
      headers: {
        'Accept': 'multipart/form-data',
        'Content-Type': 'multipart/form-data',
        'Authorization': 'Bearer ' + accessToken
      }
    })

    if (response.status == 200) {
      await this.getBoccoChannel()
    }

    const status = response.status
    return status
  }

  @Action
  async postBoccoChannelService(payload: CreateChannelService) {
    const accessToken = getModule(Meta).accessToken
    const data = new FormData() 
    
    data.append('service_name', payload.service_name)
    data.append('service_description', payload.service_description)
    data.append('service_code', payload.service_code)
    data.append('broad_caster_name', payload.broad_caster_name)
    data.append('use_voice_command', payload.use_voice_command.toString())

    if (payload.use_voice_command) {
      data.append("voice_command", payload.word!!)
    }

    if(payload.icon && payload.icon.size > 0){
      data.append('icon', payload.icon)
    }
    if (payload.channel_page_url) {
      data.append('channel_page_url', payload.channel_page_url)
    }

    const response = await axios.post(`${this.baseUrl}/v1/bocco_channel/services`, data, {
      headers: {
        'Accept': 'multipart/form-data',
        'Content-Type': 'multipart/form-data',
        'Authorization': 'Bearer ' + accessToken
      },
      validateStatus: function(status) {
        return status >= 200 && status <= 400;
      }
    })

    if (response.status == 200) {
      await this.getBoccoChannel()
    }

    return {status: response.status, data:response.data}
  }

  @Action
  async putBoccoChannelService(payload: EditServiceForm) {
    const accessToken = getModule(Meta).accessToken
    const data = new FormData() // for multipart/form-data
    
    data.append('name', payload.data.name)
    data.append('description', payload.data.description)
    if(payload.data.icon){
      data.append('icon', payload.data.icon)
    }
    if (payload.data.url) {
      data.append('url', payload.data.url)
    }
    data.append('publish', payload.data.published.toString())

    const uuid = payload.uuid
    const response = await axios.put(`${this.baseUrl}/v1/bocco_channel/services/` + uuid, data, {
      headers: {
        'Accept': 'multipart/form-data',
        'Content-Type': 'multipart/form-data',
        'Authorization': 'Bearer ' + accessToken
      }
    })
    const status = response.status
    return status
  }

  @Action
  async putBoccoChannelServiceVui(payload: EditServiceVUIForm) {
    const accessToken = getModule(Meta).accessToken
    const data = new FormData() // for multipart/form-data
    
    data.append('voice_command', payload.voiceCommand)

    const uuid = payload.uuid
    const response = await axios.put(`${this.baseUrl}/v1/bocco_channel/services/` + uuid + "/commands",  data, {
      headers: {
        'Accept': 'multipart/form-data',
        'Content-Type': 'multipart/form-data',
        'Authorization': 'Bearer ' + accessToken
      }
    })
    const status = response.status
    return status
  }

  @Action
  async putBoccoChannelServiceWebhook(payload: EditServiceWebhookForm) {
    const accessToken = getModule(Meta).accessToken
    const data = new FormData() // for multipart/form-data
    
    data.append('endpoint', payload.webhook)

    const uuid = payload.uuid
    const response = await axios.put(`${this.baseUrl}/v1/bocco_channel/services/` + uuid + "/conversation_endpoint",  data, {
      headers: {
        'Accept': 'multipart/form-data',
        'Content-Type': 'multipart/form-data',
        'Authorization': 'Bearer ' + accessToken
      }
    })
    const status = response.status
    return status
  }


  @Action
  async putBoccoChannelServiceUser(payload: EditServiceUserForm) {
    const accessToken = getModule(Meta).accessToken
    const data = new FormData() // for multipart/form-data
    
    data.append('name', payload.data.name)
    if(payload.data.icon){
      data.append('icon', payload.data.icon)
    }

    const uuid = payload.uuid
    const response = await axios.put(`${this.baseUrl}/v1/bocco_channel/services/${uuid}/user`, data, {
      headers: {
        'Accept': 'multipart/form-data',
        'Content-Type': 'multipart/form-data',
        'Authorization': 'Bearer ' + accessToken
      }
    })
    const status = response.status
    return status
  }

  @Action
  async getPayments(offset = 0) {
    const accessToken = getModule(Meta).accessToken
    const response = await axios.get(`${this.baseUrl}/v1/payments?offset=${offset}`, {
      headers: {
        'accept': 'application/json',
        'Authorization': 'Bearer ' + accessToken
      }
    })
    if (!response.data.payments.length) {
      return false
    }
    const items = Array(response.data.listing.total)
    response.data.payments.forEach((item: PaymentsDataResponse, index: number) => {
      const newIndex = index + offset
      items[newIndex] = Object.assign({}, item)
    });
    const total = response.data.listing.total
    const limit = response.data.listing.limit
    this.updatePaymentsDataOption({ total, limit })
    this.updatePaymentsData(items)
    return true
  }

  @Action
  async getPaymentsDetail(id: string) {
    const accessToken = getModule(Meta).accessToken
    const response = await axios.get(`${this.baseUrl}/v1/payments/${id}`, {
      headers: {
        'accept': 'application/json',
        'Authorization': 'Bearer ' + accessToken
      }
    })
    const payments = response.data as PaymentsDetailDataResponse
    this.updatePaymentsDetailData(payments)
  }

  @Action
  async postContacts(data: string[]) {
    const accessToken = getModule(Meta).accessToken
    const sendData = { "title": data[0], "content": data[1] }
    const response = await axios.post(`${this.baseUrl}/v1/contacts`, sendData, {
      headers: {
        'Content-Type': 'application/json',
        'accept': '*/*',
        'Authorization': 'Bearer ' + accessToken
      }

    })
    const status = response.status
    return status
  }

  @Action
  async getLoginInfo(accessToken: string) {
    const response = await axios.get(`${this.baseUrl}/v1/me`, {
      headers: {
        "Authorization": `Bearer ${accessToken}`
      }
    })
    const info = response.data as LoginInfoResponse
    this.updateLoginInfo(info)
  }

  @Action
  async putLoginInfo(payload: AccountInfo) {
    const accessToken = getModule(Meta).accessToken
    const response = await axios.put(`${this.baseUrl}/v1/me`, payload, {
      headers: {
        "accept": "application/json",
        "Content-Type": "application/json",
        "Authorization": `Bearer ${accessToken}`
      }
    })
    const status = response.status
    return status
  }

  @Action
  async putMyProfile(payload: ProfileInfo) {
    const accessToken = getModule(Meta).accessToken
    const response = await axios.put(`${this.baseUrl}/v1/me/profile`, payload, {
      headers: {
        "accept": "application/json",
        "Content-Type": "application/json",
        "Authorization": `Bearer ${accessToken}`
      }
    })
    return response.status
  }
  @Action
  async accountWithdrawal(reason: string) {
    const accessToken = getModule(Meta).accessToken
    const payload = {reason: reason}
    const response = await axios.post(`${this.baseUrl}/account/withdrawal`, payload, {
      headers: {
        "accept": "application/json",
        "Authorization": `Bearer ${accessToken}`
      }
    })
    const status = response.status
    return status
  }

  @Action({ rawError: true })
  async businessUserAuthentication(payload: IdPassAuthenticationPayload) {
    const response = await axios.post(`${this.baseUrl}/account/token`, payload)
    const auth = response.data as AuthenticationResponse

    getModule(Meta).updateAccessToken(auth.access_token)
    getModule(Meta).updateRefreshToken(auth.refresh_token)
  }

  // ==========================================
  // セルフサービス系: /v1/me/* と /invitations/*
  // ==========================================

  @Action
  async putMyPassword(payload: ChangePasswordPayload) {
    const accessToken = getModule(Meta).accessToken
    const response = await axios.put(`${this.baseUrl}/v1/me/password`, payload, {
      headers: {
        "Content-Type": "application/json",
        "Authorization": `Bearer ${accessToken}`
      }
    })
    return response.status
  }

  @Action
  async getMyMembers(): Promise<Member[]> {
    const accessToken = getModule(Meta).accessToken
    const response = await axios.get(`${this.baseUrl}/v1/me/members`, {
      headers: {
        "Authorization": `Bearer ${accessToken}`
      }
    })
    return response.data as Member[]
  }

  @Action
  async deleteMyMember(memberId: number) {
    const accessToken = getModule(Meta).accessToken
    const response = await axios.delete(`${this.baseUrl}/v1/me/members/${memberId}`, {
      headers: {
        "Authorization": `Bearer ${accessToken}`
      }
    })
    return response.status
  }

  @Action
  async getMyInvitations(): Promise<Invitation[]> {
    const accessToken = getModule(Meta).accessToken
    const response = await axios.get(`${this.baseUrl}/v1/me/invitations`, {
      headers: {
        "Authorization": `Bearer ${accessToken}`
      }
    })
    return response.data as Invitation[]
  }

  @Action
  async postMyInvitation(payload: CreateInvitationPayload): Promise<Invitation> {
    const accessToken = getModule(Meta).accessToken
    const response = await axios.post(`${this.baseUrl}/v1/me/invitations`, payload, {
      headers: {
        "Content-Type": "application/json",
        "Authorization": `Bearer ${accessToken}`
      }
    })
    return response.data as Invitation
  }

  @Action
  async deleteMyInvitation(invitationId: number) {
    const accessToken = getModule(Meta).accessToken
    const response = await axios.delete(`${this.baseUrl}/v1/me/invitations/${invitationId}`, {
      headers: {
        "Authorization": `Bearer ${accessToken}`
      }
    })
    return response.status
  }

  /**
   * 招待受諾用 (public; トークンのみで認証)
   */
  @Action
  async getInvitationInfo(token: string): Promise<InvitationInfo> {
    const response = await axios.get(`${this.baseUrl}/invitations/${token}`)
    return response.data as InvitationInfo
  }

  @Action
  async postInvitationAccept(args: { token: string; payload: AcceptInvitationPayload }): Promise<AuthenticationResponse> {
    const response = await axios.post(`${this.baseUrl}/invitations/${args.token}/accept`, args.payload, {
      headers: {
        "Content-Type": "application/json"
      }
    })
    const auth = response.data as AuthenticationResponse
    getModule(Meta).updateAccessToken(auth.access_token)
    getModule(Meta).updateRefreshToken(auth.refresh_token)
    return auth
  }
}